/** @jsx jsx */

import type { ReactElement } from 'react';
import {
  Children,
  Fragment,
  cloneElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTooltip } from '@reach/tooltip';
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx, useCore } from '@balance-web/core';
import { VisuallyHidden } from '@balance-web/a11y';
import { usePopover } from '@balance-web/popover';
import { Portal } from '@balance-web/portal';
import { useTheme } from '@balance-web/theme';
import { makeId, makeIdFactory, useForkedRef } from '@balance-web/utils';
import { DefaultTextPropsProvider } from '@balance-web/text';

import { arrowStyles } from './utils';

const TooltipTestIdFactory = makeIdFactory('tooltip');

// Screen readers announce the tooltip content (the trigger's accessible
// description) reliably only when focus lands on an interactive element —
// VoiceOver reads a labelled span as a bare "group" and skips the
// description entirely.
const INTERACTIVE_TRIGGER_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'summary',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="link"]',
  '[role="listbox"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="searchbox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
].join(', ');

const warnedTriggers = new WeakSet<Element>();

export type TooltipProps = WithDataAttributeProp<{
  /** the id  */
  id?: string;
  /** The target element. */
  children: ReactElement;
  /** The content of the tooltip. */
  content: string | ReactElement;
  /** Where, in relation to the target, to place the tooltip. */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Which (CSS) positioning strategy to use. */
  positionStrategy?: 'absolute' | 'fixed';
  /** Which (CSS) positioning strategy to use. */
  verticalOffset?: number;
}>;

export const Tooltip = ({
  children,
  content,
  placement = 'top',
  positionStrategy = 'absolute',
  verticalOffset,
  data = {},
}: TooltipProps) => {
  const testid = TooltipTestIdFactory(getTestIdFromDataAttributes(data));

  // PopperJS stuff
  const { trigger: popperTrigger, dialog, setOpen } = usePopover({
    placement,
    strategy: positionStrategy,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, verticalOffset === undefined ? 12 : verticalOffset],
        },
      },
    ],
  });

  const core = useCore();

  // Reach UI stuff
  const child = Children.only(children) as any;
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const childRef = useForkedRef(
    child.ref,
    popperTrigger.ref,
    triggerElementRef
  );

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      return;
    }
    const trigger = triggerElementRef.current;
    if (
      trigger instanceof Element &&
      !warnedTriggers.has(trigger) &&
      !trigger.matches(INTERACTIVE_TRIGGER_SELECTOR)
    ) {
      warnedTriggers.add(trigger);
      console.warn(
        `Tooltip: the trigger <${trigger.tagName.toLowerCase()}> is not an interactive element, so screen readers will not announce the tooltip content when it receives focus. Use an interactive trigger (e.g. IconButton), or HelpIndicator from @balance-web/tooltip for help icons.`,
        trigger
      );
    }
  }, []);
  const [reachTrigger, reachTooltip] = useTooltip({
    onMouseEnter: child.props.onMouseEnter,
    onMouseMove: child.props.onMouseMove,
    onMouseLeave: child.props.onMouseLeave,
    onMouseDown: child.props.onMouseDown,
    onFocus: child.props.onFocus,
    onBlur: child.props.onBlur,
    onKeyDown: child.props.onKeyDown,
    ref: childRef,
    // @ts-ignore
    disabled: child.props.disabled,
  });

  // Sync Popper's open state with Reach's, so we get the most up-to-date
  // position. Adjusted during render (not in an effect) so both state
  // machines agree within the same commit.
  const [prevIsVisible, setPrevIsVisible] = useState(reachTooltip.isVisible);
  if (prevIsVisible !== reachTooltip.isVisible) {
    setPrevIsVisible(reachTooltip.isVisible);
    setOpen(reachTooltip.isVisible);
  }

  // Reach only exposes `aria-describedby` on the trigger while the tooltip is
  // visible, i.e. one render after focus lands. VoiceOver composes its
  // announcement from the accessibility tree as it was when focus moved and
  // never re-reads late-attached descriptions, so the description must exist
  // before focus: the trigger permanently references a node that always holds
  // the content — a visually hidden span while closed, the positioned popup
  // while open. Same id format as Reach's own (`tooltip--{id}`), so the
  // reference matches what Reach sets while visible.
  const descriptionId = makeId('tooltip', reachTooltip.id);

  const combinedTriggerProps = {
    ...reachTrigger,
    'aria-describedby': descriptionId,
    tabIndex: core.fastMode ? -1 : 0,
  }; // support non-interactive elements

  return (
    <Fragment>
      {cloneElement(children, combinedTriggerProps)}

      {reachTooltip.isVisible ? (
        <TooltipElement
          {...dialog.props}
          ref={dialog.ref}
          data-testid={testid.base(descriptionId)}
          id={descriptionId}
        >
          {content}
        </TooltipElement>
      ) : (
        <VisuallyHidden id={descriptionId} role="tooltip">
          {typeof content === 'string' ? content : null}
        </VisuallyHidden>
      )}
    </Fragment>
  );
};

// Styled Component
// ------------------------------

type ElementProps = {
  /** The content of the tooltip. */
  children: string | ReactElement;
  /** ID used to describe the invoking element. */
  id?: string;
};

export const TooltipElement = forwardRef<HTMLDivElement, ElementProps>(
  ({ children, ...props }, consumerRef) => {
    const {
      elevation,
      radii,
      semantic,
      shadow,
      spacing,
      typography,
    } = useTheme();

    const lineheight = typography.leading.base;

    return (
      <Portal>
        <div
          ref={consumerRef}
          css={{
            background: semantic.colour.neutral.inverse.background,
            borderRadius: radii.xsmall,
            boxShadow: shadow.small,
            color: semantic.colour.neutral.inverse.foreground,
            fontSize: typography.fontSize.small,
            lineHeight: lineheight,
            maxWidth: 260, // less than desirable magic number, but not sure if this needs to be in theme...
            pointerEvents: 'none',
            padding: spacing.small,
            zIndex: elevation.toast,
            ...arrowStyles(semantic, shadow),
            wordWrap: 'break-word',
          }}
          role="tooltip"
          {...props}
        >
          <span
            css={{
              display: 'block',

              // offset lineheight
              '::before': {
                content: '" "',
                display: 'block',
                height: 0,
                marginTop: `calc((1 - ${lineheight}) * 0.5em)`,
              },
              '::after': {
                content: '" "',
                display: 'block',
                height: 0,
                marginBottom: `calc((1 - ${lineheight}) * 0.5em)`,
              },
            }}
          >
            <DefaultTextPropsProvider color="inherit">
              {children}
            </DefaultTextPropsProvider>
          </span>
        </div>
      </Portal>
    );
  }
);
